"use client";

/**
 * The reading-and-writing surface for `src/lib/gtaKnowledge/carcols.ts`.
 *
 * `carcols.ts` already does every judgement call this tool makes: which ids
 * collide, which are out of range, which siren profile overruns the bone limit,
 * and — the hard part — which of those can be renumbered without leaving a
 * `carvariations.meta` reference pointed at a number that no longer means
 * anything. What is left for this file is the one thing only a page can get
 * wrong, and it is the same failure four times over:
 *
 *   1. Losing the attribution `intake.ts` hands back, so two vendor packs
 *      called `police` fold into one and half their ids silently stop being
 *      checked. See `groupsOf`, which is the one-line fix and the one-line bug.
 *   2. Writing before showing. `planRenumber` is a pure function that never
 *      touches a byte; the whole point of a plan is that a reader can see every
 *      move and every refusal *before* anything downloads. So the accepted
 *      list and the refused list render as two panels of equal size, in the
 *      same visual language, and the refused one comes first — a mistake this
 *      tool would not fix is more important to see than one it would.
 *   3. Hiding a refusal behind a clean count. "12 ids fixed" said next to
 *      nothing about the 3 this tool would not touch is the exact shape of the
 *      competitor defect the brief is written against: a kit renamed with zero
 *      of its references updated, reported as success. Every id `carcolsFindings`
 *      calls broken is accounted for in exactly one of "will be renumbered" or
 *      "refused, and here is why" — never neither.
 *   4. Re-verifying at the wrong time, or not at all. `planRenumber` computes
 *      spans against the text it was handed; by the time a user clicks
 *      Download, nothing about that text has changed in this tab, but the
 *      contract of `patch.ts` is that a write is only ever as safe as its own
 *      verify step, not as safe as the plan that produced it. So the same
 *      `applyEdits` a stale re-run would need is what actually builds the
 *      patched text here, and its own rejections — which should be empty, and
 *      are shown if they are not — travel into the manifest rather than being
 *      swallowed by a caller that trusts the plan alone.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT "FIXED" MEANS ON THIS PAGE — A REAL BEFORE AND AFTER
 *
 * The brief's fifth point against the competitor is that its panel keeps
 * showing the pre-patch world with the Apply button still live, so the only way
 * to confirm anything worked is to re-pick the folder. This page re-runs
 * `indexCarcols` and `carcolsFindings` over the patched text — not the request,
 * the actual output of `applyEdits` — every time the accepted set of moves
 * changes, and prints both counts. That is `patchedInputs`/`afterFindings`
 * below: nothing here computes "how many findings this *should* clear" from the
 * plan, because that number and the true one can only differ in the direction
 * that matters — a move whose target id happens to collide with something else
 * accepted in the same batch, which only a re-scan of the actual result would
 * catch.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A DOWNLOAD, AND WHY IT IS SMALL
 *
 * The owner made both Phase 4 tools free and public rather than gated, but nothing
 * about the safety story changes: this is still the first tool in the plan that
 * writes bytes, and being free removes the account, not the care. So there is no
 * upload path anywhere in this file's import graph, and the write itself is
 * scoped as narrowly as the edits are: the zip this page offers holds only the
 * `carcols.meta`/`carvariations.meta` files an accepted move actually touched,
 * each paired with a `.bak` of the untouched original — never the whole pack,
 * which this page never even asks the browser to keep in memory as bytes (only
 * text files are decoded by `collect.ts` in the first place). A `.bak` that
 * already exists in the resource is left alone rather than overwritten, exactly
 * as the brief asks for the in-place case: re-running this tool, or a
 * competitor's, must never destroy the one copy of the true original.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LIMITS — read before trusting a clean plan
 *
 * This is `carcols.ts`'s own scope, unwidened. It reads `carcols.meta` and
 * `carvariations.meta` only: a mod kit's `kitName` is never rewritten (see that
 * module's header for why touching only the id is what keeps a kit's binding
 * intact), nothing outside those two files is touched, and a resource whose
 * `carvariations.meta` this scan could not parse gets every one of its siren and
 * light moves refused rather than guessed at. It renders one scan; picking again
 * replaces it, and there is no "add another folder" — `collectResources` can
 * only promise unique identities within one drop, and unioning two picks by
 * hand is exactly how two packs called `police` fold into one silently.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SKIP LIST — ONE LIST, WHATEVER THE GESTURE
 *
 * The brief asks for one skip list across all three intake paths, because the
 * competitor honoured its own on one path of three and the same tree produced
 * three plans. So the list is applied by `excludeResources` in `intake.ts`, to
 * the `ResourceDrop` every gesture has already become, and never inside a
 * gesture: this page keeps the drop and re-runs `scanDrop` over it whenever a
 * box is ticked, so leaving a backup copy out never means re-picking a narrower
 * folder, and a drag-drop cannot disagree with a folder pick about what was
 * skipped.
 *
 * Leaving a resource out is a claim about input, so it is said where every other
 * unread thing is said — the notice list, `FindingsList`'s not-checked line, the
 * report and the manifest — never only by the absence of a tick. And it has one
 * consequence `excludeResources` cannot know about, which `excludedIdNotices`
 * states: a left-out resource's own ids were never seen, so an id this plan hands
 * out may already be taken inside it, and a siren or light id renumbered here is
 * not updated there. The skip is honoured anyway, because the user who ticks it
 * is almost always removing a copy the server does not load; the sentence is
 * what stops that from being a silent assumption.
 *
 * The tour is in manual mode, raised by the sample button and by the permanent
 * "Show me how this works" control, never by a report appearing — see
 * `ShowTourButton`.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Download,
  FileJson,
  FileText,
  FolderOpen,
  ShieldAlert,
  Upload,
} from "lucide-react";
import { FindingsList } from "@/components/gta/FindingsList";
import { ShowTourButton } from "@/components/freeTools/ShowTourButton";
import { ToolWalkthrough } from "@/components/walkthrough/ToolWalkthrough";
import {
  finishLabel as idBenchFinishLabel,
  steps as idBenchSteps,
} from "@/components/walkthrough/steps/idBench";
import { downloadTextFile } from "@/lib/dom";
import {
  carcolsFindings,
  groupEditsByFile,
  indexCarcols,
  planRenumber,
  type CarcolsIndex,
  type RenumberMove,
  type RenumberPlan,
  type RenumberRefusal,
} from "@/lib/gtaKnowledge/carcols";
import { collectResources, type CollectableGroup } from "@/lib/gtaKnowledge/collect";
import { DEMO_FILENAME_PREFIX, DEMO_INPUT_NOTE, DEMO_STATE_LABEL } from "@/lib/gtaKnowledge/demoPacks";
import { sortFindings } from "@/lib/gtaKnowledge/findings";
import { ambulanceOutOfRangeCarcols } from "@/lib/gtaKnowledge/fixtures";
import {
  excludeResources,
  fromArchives,
  fromDataTransfer,
  fromDirectoryPick,
  type DataTransferLike,
  type ExcludedResource,
  type ResourceDrop,
} from "@/lib/gtaKnowledge/intake";
import { disambiguateLabels, PROSE_LABEL_STYLE } from "@/lib/gtaKnowledge/labels";
import { ID_POOLS, explainAllocation } from "@/lib/gtaKnowledge/modkits";
import { baseName, pathKey } from "@/lib/gtaKnowledge/normalize";
import { applyEdits, dryRunDiff } from "@/lib/gtaKnowledge/patch";
import { GTA_REPORT_STATIC_CHECK_NOTE, GTA_REPORT_TOOL } from "@/lib/gtaKnowledge/report";
import type { GtaFinding, GtaResourceInput, GtaTextEdit } from "@/lib/gtaKnowledge/types";
import { pickZipSaveTarget, writeZip, type ZipEntry } from "@/lib/zipStream";

// ─── Caps ────────────────────────────────────────────────────────────────────
//
// Exported for the same reason `SpawnIndexClient.tsx` exports its own: a test
// asserts against the number the page actually uses, not a copy of it.

/** Move / refusal rows drawn before the reveal appears. */
export const ID_BENCH_ROWS_VISIBLE = 40;

/** How many more rows one click of a reveal mounts. Never "all of them". */
export const ID_BENCH_ROWS_PAGE = 200;

// ─── Attribution — the one-line mistake this file must not make ────────────

/**
 * `ResourceDrop` → the shape `collectResources` wants, root and origin both
 * carried through.
 *
 * Identical to `groupsOf` in `ClashFinderClient.tsx` and `SpawnIndexClient.tsx`.
 * Not shared, on purpose: it is four lines, and importing it from a sibling
 * free-tool client would tie this page's compile to that one's for no reuse
 * worth the coupling. The comment travels with the code every place it is
 * pasted so the reason survives the copy.
 */
export function groupsOf(drop: ResourceDrop): CollectableGroup[] {
  return drop.resources.map((resource) => {
    const origin = drop.origins.get(resource.root);
    return {
      name: resource.name,
      files: resource.files,
      root: resource.root,
      ...(origin !== undefined && origin !== "" ? { origin } : {}),
    };
  });
}

// ─── The scan ────────────────────────────────────────────────────────────────

export type IdBenchScan = {
  inputs: GtaResourceInput[];
  /** Everything intake and the collector could not read, in their own words. */
  notices: string[];
  /** Every path the drop held, including the ones no resource claimed. */
  files: number;
  /** Files the collector actually decoded as text, counted off what it returned. */
  opened: number;
  /**
   * Resources left out through the skip list. Each is also a sentence in
   * `notices`; this is the same fact as data, so the manifest can list it.
   */
  excluded: ExcludedResource[];
};

/** Wraps already-collected resources. Shared by the real intake path and the demo. */
function scanFromInputs(
  inputs: GtaResourceInput[],
  notices: string[],
  files: number,
  excluded: ExcludedResource[] = [],
): IdBenchScan {
  return {
    inputs,
    notices: [...new Set(notices)],
    files,
    opened: inputs.reduce((total, input) => total + input.texts.size, 0),
    excluded,
  };
}

/** The two files this tool reads ids out of, by bare name. */
const ID_FILE_NAMES: ReadonlySet<string> = new Set(["carcols.meta", "carvariations.meta"]);

/**
 * What leaving a resource out costs *this* tool, per resource it applies to.
 *
 * `excludeResources` already says the resource was not read. What it cannot
 * say is the id-specific consequence, and that consequence is the whole reason
 * this tool exists: ids are global across a server, so a left-out resource that
 * defines or references ids is invisible to both halves of the plan — an id
 * allocated here may already be taken inside it, and a siren or light id moved
 * here keeps its old number there. Only said for a resource that actually ships
 * one of the two files, read off the inventory the drop already holds, so a
 * left-out script resource does not grow a warning about ids it cannot have.
 */
export function excludedIdNotices(drop: ResourceDrop, excluded: readonly ExcludedResource[]): string[] {
  const byRoot = new Map(drop.resources.map((resource) => [pathKey(resource.root), resource]));
  const notices: string[] = [];
  for (const entry of excluded) {
    const resource = byRoot.get(pathKey(entry.root));
    const holdsIds = resource?.files.some((file) => ID_FILE_NAMES.has(baseName(pathKey(file.path))));
    if (!holdsIds) continue;
    notices.push(
      `${entry.label} has its own carcols or carvariations file and was left out, so none of its ids ` +
        "were checked against this scan: an id this plan hands out may already be taken inside it, and a " +
        "siren or light id renumbered here is not updated there. If your server loads that resource, " +
        "include it again before downloading.",
    );
  }
  return notices;
}

/**
 * A `ResourceDrop`, read end to end, minus anything on the skip list.
 *
 * Exported so a test can drive the real pipeline — intake, then collection —
 * without a browser gesture, which is the only way a test can actually catch
 * the attribution bug `groupsOf` exists to avoid.
 *
 * `skip` holds `DroppedResource.root`s and goes through `excludeResources`
 * before anything is read, so a left-out resource's text is never decoded and
 * its sentence sits in `notices` beside every other unread thing.
 */
export async function scanDrop(drop: ResourceDrop, skip: Iterable<string> = []): Promise<IdBenchScan> {
  const read = excludeResources(drop, skip);
  const excluded = read.excluded ?? [];
  const { inputs, truncated } = await collectResources(groupsOf(read));
  return scanFromInputs(
    inputs,
    [...read.truncated, ...excludedIdNotices(drop, excluded), ...truncated],
    drop.allPaths.length,
    excluded,
  );
}

// ─── Resource labels, for the plan and the manifest ─────────────────────────

/** `identity ?? name`, normalised — the same key `carcols.ts` stamps onto every move. */
function resourceKeyOf(input: GtaResourceInput): string {
  return (input.identity && input.identity !== "" ? input.identity : input.name).toLowerCase();
}

function labelsFor(inputs: readonly GtaResourceInput[]): ReadonlyMap<string, string> {
  const labels = disambiguateLabels(inputs, PROSE_LABEL_STYLE);
  const byKey = new Map<string, string>();
  inputs.forEach((input, index) => {
    byKey.set(resourceKeyOf(input), labels[index] ?? input.name);
  });
  return byKey;
}

// ─── Applying the accepted moves — the one place this page writes anything ──

type PatchedFile = {
  resourceKey: string;
  resourceLabel: string;
  /** Resource-relative, lower-cased — the same spelling `carcols.ts` gives `edit.file`. */
  file: string;
  originalText: string;
  patchedText: string;
  appliedCount: number;
  rejected: { edit: GtaTextEdit; reason: string }[];
  /** Whether `<file>.bak` already exists in this resource's own inventory. */
  bakAlreadyExists: boolean;
};

/**
 * Every accepted move's edits, applied, grouped so that two resources sharing
 * a file name — two packs each with their own `carcols.meta` — never share a
 * verification pass.
 *
 * `groupEditsByFile` does that grouping itself now, keyed on the resource *and*
 * the file, and this walks the result. It used to group by `move.resource`
 * here instead, which looked equivalent and was not: a siren or light id is
 * global, so one move's edits can span two resources — the definition in A's
 * `carcols.meta`, the cascade in B's `carvariations.meta` — and looking every
 * edit up under the mover's resource checked B's edit against A's same-named
 * file. `applyEdits` refused it on the text mismatch, the loop skipped the file
 * because nothing applied, and the refusal never reached `rejectedAtWriteTime`
 * or the "could not be verified" banner. The definition moved and the real
 * reference was left dangling, reported as clean.
 *
 * A file whose edits ALL failed verification is still recorded, with
 * `appliedCount: 0`, precisely so that failure can never be silent again — the
 * caller counts rejections across every entry and the page refuses to download.
 */
function applyAcceptedMoves(
  moves: readonly RenumberMove[],
  inputs: readonly GtaResourceInput[],
): PatchedFile[] {
  const inputByKey = new Map(inputs.map((input) => [resourceKeyOf(input), input]));
  const labels = labelsFor(inputs);

  const patched: PatchedFile[] = [];
  for (const { resource, file, edits } of groupEditsByFile(moves).values()) {
    const resourceKey = resource.toLowerCase();
    const input = inputByKey.get(resourceKey);
    if (!input) continue; // Cannot happen for a move this scan produced; guarded rather than assumed.
    const original = input.texts.get(file);
    if (original === undefined) continue; // Same guard, same reason.
    const result = applyEdits(original, edits);
    const bakPath = `${file}.bak`;
    const bakAlreadyExists = input.files.some((entry) => pathKey(entry.path) === pathKey(bakPath));
    patched.push({
      resourceKey,
      resourceLabel: labels.get(resourceKey) ?? input.name,
      file,
      originalText: original,
      patchedText: result.text,
      appliedCount: result.applied.length,
      rejected: result.rejected,
      bakAlreadyExists,
    });
  }
  return patched;
}

/** `inputs`, with every patched file's text swapped in — for the real before/after. */
function withPatchedTexts(
  inputs: readonly GtaResourceInput[],
  patched: readonly PatchedFile[],
): GtaResourceInput[] {
  if (patched.length === 0) return [...inputs];
  const byResource = new Map<string, Map<string, string>>();
  for (const entry of patched) {
    const held = byResource.get(entry.resourceKey);
    if (held) held.set(entry.file, entry.patchedText);
    else byResource.set(entry.resourceKey, new Map([[entry.file, entry.patchedText]]));
  }
  return inputs.map((input) => {
    const overrides = byResource.get(resourceKeyOf(input));
    if (!overrides) return input;
    const texts = new Map(input.texts);
    for (const [file, text] of overrides) texts.set(file, text);
    return { ...input, texts };
  });
}

// ─── The manifest and the report — what leaves the tab alongside the files ──

function moveRow(move: RenumberMove) {
  return {
    pool: move.pool,
    resource: move.resource,
    from: move.from,
    to: move.to,
    ...(move.kitName !== undefined ? { kitName: move.kitName } : {}),
    reason: move.reason,
    allocation: explainAllocation(move.pool, move.to),
    files: [...new Set(move.edits.map((e) => `${e.resource}/${e.edit.file}`))],
  };
}

function refusalRow(refusal: RenumberRefusal) {
  return { pool: refusal.pool, resource: refusal.resource, id: refusal.id, reason: refusal.reason };
}

function buildManifest(options: {
  ssla: boolean;
  accepted: RenumberMove[];
  declined: RenumberMove[];
  refused: readonly RenumberRefusal[];
  patched: readonly PatchedFile[];
  before: number;
  after: number;
  excluded: readonly ExcludedResource[];
  notices: readonly string[];
}): string {
  const { ssla, accepted, declined, refused, patched, before, after, excluded, notices } = options;
  const rejectedEdits = patched.flatMap((entry) =>
    entry.rejected.map((rejection) => ({ file: entry.file, resource: entry.resourceLabel, reason: rejection.reason })),
  );
  const manifest = {
    tool: GTA_REPORT_TOOL,
    schema: "id-bench-manifest/v1",
    generatedAt: new Date().toISOString(),
    staticCheckNote: GTA_REPORT_STATIC_CHECK_NOTE,
    ssla,
    // Left out through the skip list: not read, so no id in them was checked
    // and no reference in them was updated. A manifest that listed only what
    // moved would read as an audit of the whole drop.
    excluded: excluded.map((entry) => ({
      resource: entry.label,
      root: entry.root,
      ...(entry.origin !== undefined ? { origin: entry.origin } : {}),
      filesNotRead: entry.files,
    })),
    /** Everything intake, the collector and the skip list did not read, verbatim. */
    notRead: [...notices],
    findingsBefore: before,
    findingsAfter: after,
    accepted: accepted.map(moveRow),
    declined: declined.map(moveRow),
    refused: refused.map(refusalRow),
    /** Should always be empty. Present because a claim this file makes has to be checkable. */
    rejectedAtWriteTime: rejectedEdits,
    // Only the files the zip actually carries. `patched` also holds files where
    // every edit failed verification — recorded so `rejectedAtWriteTime` above
    // can never be silently empty — and claiming a backup for a file that is
    // not in the archive would be the same kind of lie in the other direction.
    backups: patched
      .filter((entry) => entry.appliedCount > 0)
      .map((entry) => ({
        resource: entry.resourceLabel,
        file: entry.file,
        backupWritten: !entry.bakAlreadyExists,
        note: entry.bakAlreadyExists
          ? `${entry.file}.bak already exists in this resource; the original text is preserved there ` +
            "and this run did not overwrite it or write a second backup."
          : `${entry.file}.bak holds the text exactly as this scan read it, before any of these edits.`,
      })),
  };
  return JSON.stringify(manifest, null, 2);
}

function buildReport(options: {
  ssla: boolean;
  accepted: RenumberMove[];
  declined: RenumberMove[];
  refused: readonly RenumberRefusal[];
  before: number;
  after: number;
  excluded: readonly ExcludedResource[];
  notices: readonly string[];
}): string {
  const { ssla, accepted, declined, refused, before, after, excluded, notices } = options;
  const lines: string[] = [];
  lines.push(`# ${GTA_REPORT_TOOL} — ID Bench renumber report`);
  lines.push("");
  lines.push(`Generated ${new Date().toISOString()}.`);
  lines.push("");
  lines.push(`> ${GTA_REPORT_STATIC_CHECK_NOTE}`);
  lines.push("");
  lines.push(
    `Siren Setting Limit Adjuster: ${ssla ? "assumed present" : "assumed not present"} — this changes ` +
      "the safe siren range and what counts as over the bone limit.",
  );
  lines.push("");
  lines.push(`Findings before: ${before}. Findings after the accepted moves below: ${after}.`);
  lines.push("");
  // Before the moves, not after them: a reader deciding whether to trust the
  // list below needs to know first what it was not computed over.
  if (excluded.length > 0) {
    lines.push(`## Left out of this scan by request (${excluded.length})`);
    lines.push("");
    lines.push("Not read, so no id in these was checked and no reference in them was updated.");
    lines.push("");
    for (const entry of excluded) {
      lines.push(`- **${entry.label}** (\`${entry.root}\`) — ${count(entry.files, "file")} not read.`);
    }
    lines.push("");
  }
  if (notices.length > 0) {
    lines.push(`## Not read, or read only in part (${notices.length})`);
    lines.push("");
    for (const notice of notices) lines.push(`- ${notice}`);
    lines.push("");
  }
  lines.push(`## Renumbered (${accepted.length})`);
  lines.push("");
  if (accepted.length === 0) {
    lines.push("Nothing was accepted for renumbering.");
  }
  for (const move of accepted) {
    const label = ID_POOLS[move.pool].label;
    lines.push(
      `- **${move.resource}** — ${label}${move.kitName ? ` "${move.kitName}"` : ""} ${move.from} → ` +
        `${move.to}, in ${[...new Set(move.edits.map((e) => `${e.resource}/${e.edit.file}`))].join(", ")}.`,
    );
    lines.push(`  Why it moved: ${move.reason}`);
    lines.push(`  Why ${move.to}: ${explainAllocation(move.pool, move.to)}`);
  }
  if (declined.length > 0) {
    lines.push("");
    lines.push(`## Left unchecked, not applied (${declined.length})`);
    lines.push("");
    lines.push("These were offered and could have been renumbered; they were not selected here.");
    for (const move of declined) {
      const label = ID_POOLS[move.pool].label;
      lines.push(`- **${move.resource}** — ${label} ${move.from} (would move to ${move.to}): ${move.reason}`);
    }
  }
  lines.push("");
  lines.push(`## Refused — not touched, and the reason it was refused (${refused.length})`);
  lines.push("");
  if (refused.length === 0) {
    lines.push("Nothing was refused.");
  }
  for (const refusal of refused) {
    lines.push(
      `- **${refusal.resource}** — ${ID_POOLS[refusal.pool].label} id ${refusal.id}: ${refusal.reason}`,
    );
  }
  lines.push("");
  return lines.join("\n");
}

// ─── Small helpers ───────────────────────────────────────────────────────────

function n(value: number): string {
  return value.toLocaleString("en-US");
}

function count(value: number, singular: string, many?: string): string {
  return `${n(value)} ${value === 1 ? singular : (many ?? `${singular}s`)}`;
}

/** A move's identity within one plan — stable for exactly as long as the plan is. */
function moveKey(move: RenumberMove, index: number): string {
  return `${move.pool}|${move.resource}|${move.from}|${move.to}|${index}`;
}

// ─── Phase ───────────────────────────────────────────────────────────────────

type Phase =
  | { kind: "idle" }
  | { kind: "reading" }
  | { kind: "ready"; scan: IdBenchScan }
  | { kind: "failed"; message: string };

export function IdBenchClient() {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [demo, setDemo] = useState(false);
  const [ssla, setSsla] = useState(false);
  const [deselected, setDeselected] = useState<ReadonlySet<string>>(new Set<string>());
  const [movesShown, setMovesShown] = useState(ID_BENCH_ROWS_VISIBLE);
  const [refusedShown, setRefusedShown] = useState(ID_BENCH_ROWS_VISIBLE);
  const [dragging, setDragging] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  /** The drop behind the scan on screen, kept so the skip list can re-read it. Null for the sample. */
  const [drop, setDrop] = useState<ResourceDrop | null>(null);
  /** `pathKey`s of the roots left out. Reset by every new pick. */
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(new Set<string>());
  /** True while a skip-list change is being re-read; the downloads wait for it. */
  const [rescanning, setRescanning] = useState(false);
  /** Raised by the sample button and the replay button only. See the mount at the bottom. */
  const [tourSignal, setTourSignal] = useState(0);
  const intakeAbort = useRef<AbortController | null>(null);

  const scan = phase.kind === "ready" ? phase.scan : null;

  /** Everything a new scan invalidates. Move keys are plan indices, so a stale selection would select the wrong move. */
  const resetSelection = useCallback(() => {
    setDeselected(new Set<string>());
    setMovesShown(ID_BENCH_ROWS_VISIBLE);
    setRefusedShown(ID_BENCH_ROWS_VISIBLE);
    setDownloadError(null);
  }, []);

  const begin = useCallback(
    async (read: (signal: AbortSignal) => Promise<ResourceDrop>, fromDemo = false) => {
      intakeAbort.current?.abort();
      const controller = new AbortController();
      intakeAbort.current = controller;
      setPhase({ kind: "reading" });
      setDemo(fromDemo);
      setDrop(null);
      setSkipped(new Set<string>());
      setRescanning(false);
      resetSelection();
      try {
        const nextDrop = await read(controller.signal);
        if (controller.signal.aborted) return;
        const next = await scanDrop(nextDrop);
        if (controller.signal.aborted) return;
        setDrop(nextDrop);
        setPhase({ kind: "ready", scan: next });
      } catch (error) {
        if (controller.signal.aborted) return;
        setPhase({ kind: "failed", message: error instanceof Error ? error.message : String(error) });
      }
    },
    [resetSelection],
  );

  /**
   * The skip list, applied: the same drop, read again without the roots in
   * `next`. The previous scan stays on screen while it runs — a panel that
   * blanked on every tick would lose the reader's place in a 300-row list — and
   * the downloads are disabled until the new one lands, so nothing can be saved
   * from a plan that still includes a resource the reader just left out.
   */
  const applySkip = useCallback(
    async (next: ReadonlySet<string>) => {
      if (!drop) return;
      intakeAbort.current?.abort();
      const controller = new AbortController();
      intakeAbort.current = controller;
      setSkipped(next);
      setRescanning(true);
      resetSelection();
      try {
        const scanned = await scanDrop(drop, next);
        if (controller.signal.aborted) return;
        setPhase({ kind: "ready", scan: scanned });
      } catch (error) {
        if (controller.signal.aborted) return;
        setPhase({ kind: "failed", message: error instanceof Error ? error.message : String(error) });
      } finally {
        if (!controller.signal.aborted) setRescanning(false);
      }
    },
    [drop, resetSelection],
  );

  const onFolder = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    void begin((signal) => fromDirectoryPick(files, { signal }));
  };
  const onArchives = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    void begin((signal) => fromArchives(Array.from(files), { signal }));
  };
  const onDrop = (dt: DataTransferLike | null) => {
    setDragging(false);
    if (!dt) return;
    void begin((signal) => fromDataTransfer(dt, { signal }));
  };

  /**
   * The one built-in sample: the real published `20ambocr` ambulance, with its
   * real `sirenSettings 289563` / `lightSettings 684` — the exact pack the
   * brief names as the case a range check exists for. `ambulanceOutOfRangeCarcols`
   * already returns a decoded `GtaResourceInput`, so this bypasses `intake.ts`
   * rather than round-tripping it through a fake `File`; nothing about that
   * skips the attribution rule above, because a one-resource demo has nothing
   * to disambiguate from.
   */
  const loadDemo = useCallback(() => {
    intakeAbort.current?.abort();
    const input = ambulanceOutOfRangeCarcols();
    setPhase({ kind: "ready", scan: scanFromInputs([input], [], input.files.length) });
    setDemo(true);
    setSsla(false);
    // One resource and no drop, so there is nothing to leave out.
    setDrop(null);
    setSkipped(new Set<string>());
    setRescanning(false);
    resetSelection();
    // The tour opens over the sample it describes; see the mount at the bottom.
    setTourSignal((signal) => signal + 1);
  }, [resetSelection]);

  // ── The index, the findings, and the plan — all pure, all re-derived ───────

  const index = useMemo<CarcolsIndex | null>(() => (scan ? indexCarcols(scan.inputs) : null), [scan]);

  const findings = useMemo<GtaFinding[]>(
    () => (index ? sortFindings(carcolsFindings(index, { ssla })) : []),
    [index, ssla],
  );

  const plan = useMemo<RenumberPlan | null>(
    () => (index ? planRenumber(index, { ssla }) : null),
    [index, ssla],
  );

  const accepted = useMemo<RenumberMove[]>(
    () => (plan ? plan.moves.filter((move, i) => !deselected.has(moveKey(move, i))) : []),
    [plan, deselected],
  );
  const declined = useMemo<RenumberMove[]>(
    () => (plan ? plan.moves.filter((move, i) => deselected.has(moveKey(move, i))) : []),
    [plan, deselected],
  );

  const patched = useMemo<PatchedFile[]>(
    () => (scan ? applyAcceptedMoves(accepted, scan.inputs) : []),
    [scan, accepted],
  );

  const afterFindings = useMemo<GtaFinding[]>(() => {
    if (!scan) return [];
    const patchedInputs = withPatchedTexts(scan.inputs, patched);
    return carcolsFindings(indexCarcols(patchedInputs), { ssla });
  }, [scan, patched, ssla]);

  const rejectedAtWriteTime = useMemo(
    () => patched.flatMap((entry) => entry.rejected.map((r) => ({ ...r, file: entry.file, resource: entry.resourceLabel }))),
    [patched],
  );

  const labels = useMemo<ReadonlyMap<string, string>>(
    () => (scan ? labelsFor(scan.inputs) : new Map<string, string>()),
    [scan],
  );

  const filesToDownload = useMemo(() => patched.filter((entry) => entry.appliedCount > 0), [patched]);

  /** The skip list's row labels, disambiguated against the whole drop the same way the notices are. */
  const dropLabels = useMemo<string[]>(
    () =>
      drop
        ? disambiguateLabels(
            drop.resources.map((resource) => ({
              name: resource.name,
              identity: resource.root,
              origin: drop.origins.get(resource.root),
            })),
            PROSE_LABEL_STYLE,
          )
        : [],
    [drop],
  );

  /**
   * Accepted edits, grouped by exactly the pair the diff needs: which resource
   * and which file. Keyed on the file path alone this would do to the diff
   * preview what an ungrouped `applyEdits` batch would do to the write itself —
   * show resource B's edits laid over resource A's `carcols.meta` the moment
   * two resources share a file name.
   *
   * The resource comes off the *edit*, never off `move.resource`: one siren
   * move's edits can span two resources, so keying the cascade under the mover
   * would put B's `carvariations.meta` edit in A's diff — the preview telling
   * the same lie the write path used to.
   */
  const acceptedEditsByResourceFile = useMemo(() => {
    const map = new Map<string, GtaTextEdit[]>();
    for (const move of accepted) {
      for (const { resource, edit } of move.edits) {
        const key = `${resource.toLowerCase()}|${edit.file}`;
        const held = map.get(key);
        if (held) held.push(edit);
        else map.set(key, [edit]);
      }
    }
    return map;
  }, [accepted]);

  // ── Downloads ────────────────────────────────────────────────────────────

  const reportText = useMemo(() => {
    if (!plan) return "";
    return buildReport({
      ssla,
      accepted,
      declined,
      refused: plan.refused,
      before: findings.length,
      after: afterFindings.length,
      excluded: scan?.excluded ?? [],
      notices: scan?.notices ?? [],
    });
  }, [plan, scan, ssla, accepted, declined, findings.length, afterFindings.length]);

  const downloadReport = () => {
    const name = demo ? `${DEMO_FILENAME_PREFIX}id-bench-report.md` : "id-bench-report.md";
    downloadTextFile(name, reportText, "text/markdown");
  };

  const downloadZip = async () => {
    if (!plan || !scan || rescanning) return;
    setDownloadError(null);
    // `pickZipSaveTarget` must be the first `await` in this handler so the save
    // picker still gets the click's user-activation; the entries below are
    // cheap (they are already computed) so nothing worth awaiting comes first.
    const nameBase = demo ? `${DEMO_FILENAME_PREFIX}id-bench-changes` : "id-bench-changes";
    try {
      const target = await pickZipSaveTarget(`${nameBase}.zip`);
      if (target.kind === "cancelled") return;

      const manifest = buildManifest({
        ssla,
        accepted,
        declined,
        refused: plan.refused,
        patched,
        before: findings.length,
        after: afterFindings.length,
        excluded: scan.excluded,
        notices: scan.notices,
      });

      const entries: ZipEntry[] = [
        { path: "id-bench-report.md", source: reportText },
        { path: "id-bench-manifest.json", source: manifest },
      ];
      for (const entry of filesToDownload) {
        const base = `${entry.resourceLabel.replace(/[\\/]/g, "-")}/${entry.file}`;
        entries.push({ path: base, source: entry.patchedText });
        if (!entry.bakAlreadyExists) entries.push({ path: `${base}.bak`, source: entry.originalText });
      }

      const result = await writeZip(entries, { target });
      if (!result.savedToDisk && result.url) {
        const anchor = document.createElement("a");
        anchor.href = result.url;
        anchor.download = `${nameBase}.zip`;
        (document.body ?? document.documentElement).appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(result.url);
      }
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : String(error));
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-8">
      {/* ── Intake ──────────────────────────────────────────────────────── */}
      <section
        data-testid="id-bench-drop"
        data-walkthrough="idb-drop"
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          onDrop(event.dataTransfer as unknown as DataTransferLike);
        }}
        className={`not-prose rounded-2xl border-2 border-dashed p-5 transition-colors sm:p-6 ${
          dragging ? "border-primary/60 bg-primary/5" : "border-border/70 bg-muted/30"
        }`}
      >
        <h2 className="text-base font-semibold text-foreground">
          Drop your <code className="font-mono">resources/</code> folder, a single resource, or the
          zips you bought
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground">
          Only <code className="font-mono">carcols.meta</code> and{" "}
          <code className="font-mono">carvariations.meta</code> are read for ids; every other file is
          counted by name and size and never opened.{" "}
          <strong className="text-foreground">Nothing you drop leaves this tab</strong> — the only
          output is a zip you choose to save, and it holds only the files an accepted move actually
          changed.
        </p>

        <div className="mt-4 flex flex-wrap gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90">
            <FolderOpen className="h-4 w-4" />
            Pick a folder
            <input
              data-testid="id-bench-folder"
              type="file"
              multiple
              className="sr-only"
              onChange={(event) => onFolder(event.target.files)}
              {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
            />
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:bg-muted/40">
            <Upload className="h-4 w-4" />
            Pick zips
            <input
              data-testid="id-bench-archives"
              type="file"
              multiple
              accept=".zip"
              className="sr-only"
              onChange={(event) => onArchives(event.target.files)}
            />
          </label>
          {/* In the intake row and gated on nothing — see `ShowTourButton`. The
              sample panel below disappears once a report exists, so it cannot
              be the only way in. */}
          <ShowTourButton
            testId="id-bench-show-tour"
            onClick={() => setTourSignal((signal) => signal + 1)}
          />
        </div>

        {phase.kind !== "ready" && (
          <div className="mt-4 rounded-xl border border-border/60 bg-background/60 p-4">
            <p className="text-xs font-medium text-foreground">
              No folder to hand? Load the real, published ambulance pack whose siren id (289563) and
              light id (684) are the case this tool exists for.
            </p>
            <button
              type="button"
              data-testid="id-bench-demo"
              onClick={loadDemo}
              className="mt-3 rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary/40"
            >
              Load the 20ambocr sample
            </button>
          </div>
        )}

        {phase.kind === "reading" && <p className="mt-4 text-sm text-muted-foreground">Reading…</p>}
        {phase.kind === "failed" && (
          <p className="mt-4 text-sm text-destructive">{phase.message}</p>
        )}
      </section>

      {scan && index && plan && (
        <>
          {demo && (
            <div
              data-testid="id-bench-demo-badge"
              className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-foreground"
            >
              <span className="font-semibold">{DEMO_STATE_LABEL}.</span> {DEMO_INPUT_NOTE}
            </div>
          )}

          {/* ── Scope summary ─────────────────────────────────────────────── */}
          <section
            data-testid="id-bench-scope"
            data-walkthrough="idb-scope"
            className="rounded-2xl border border-border/60 bg-card p-5"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {count(scan.inputs.length, "resource")} read, {count(scan.opened, "text file")} opened
                of {n(scan.files)} path{scan.files === 1 ? "" : "s"} seen. {count(index.kits.length, "mod kit")},{" "}
                {count(index.sirens.length, "siren profile")}, {count(index.lights.length, "light profile")}.
              </p>
              <label className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
                <input
                  type="checkbox"
                  data-testid="id-bench-ssla"
                  checked={ssla}
                  onChange={(event) => setSsla(event.target.checked)}
                  className="h-4 w-4 rounded border-border"
                />
                This server runs the Siren Setting Limit Adjuster
              </label>
            </div>
            {/* ── The skip list. Offered only when there is a choice to make:
                a one-resource drop left with nothing in it is not a scan. The
                sentence about each left-out resource is not here — it rides
                `scan.notices` just below, so it is said in the same place, in
                the same words, as everything else this scan did not read. */}
            {drop && drop.resources.length > 1 && (
              <div data-testid="id-bench-skip-list" className="mt-3 border-t border-border/50 pt-3">
                <p className="text-sm font-medium text-foreground">
                  Resources in this scan ({n(drop.resources.length - skipped.size)} of{" "}
                  {n(drop.resources.length)} included)
                </p>
                <p className="mt-1 max-w-2xl text-xs leading-6 text-muted-foreground">
                  Untick a resource to leave it out — a backup copy, or a pack this server does not
                  load. Anything left out is named below and in both downloads.
                </p>
                <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto pr-1">
                  {drop.resources.map((resource, i) => {
                    const key = pathKey(resource.root);
                    return (
                      <li key={resource.root}>
                        <label className="inline-flex items-center gap-2 text-xs text-foreground">
                          <input
                            type="checkbox"
                            data-testid="id-bench-resource"
                            data-root={resource.root}
                            checked={!skipped.has(key)}
                            disabled={rescanning}
                            onChange={(event) => {
                              const next = new Set(skipped);
                              if (event.target.checked) next.delete(key);
                              else next.add(key);
                              void applySkip(next);
                            }}
                            className="h-3.5 w-3.5 rounded border-border"
                          />
                          <span className="font-mono">{dropLabels[i] ?? resource.name}</span>
                          <span className="text-muted-foreground">{count(resource.files.length, "file")}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                {rescanning && <p className="mt-2 text-xs text-muted-foreground">Re-reading…</p>}
              </div>
            )}
            {scan.notices.length > 0 && (
              <ul className="mt-3 space-y-1 border-t border-border/50 pt-3 text-xs text-muted-foreground">
                {scan.notices.map((notice, i) => (
                  <li key={i}>{notice}</li>
                ))}
              </ul>
            )}
          </section>

          {/* ── Findings, before anything is changed ─────────────────────── */}
          <section data-testid="id-bench-findings" data-walkthrough="idb-findings">
            <h2 className="mb-3 text-base font-semibold text-foreground">
              What this scan found ({count(findings.length, "finding")})
            </h2>
            <FindingsList findings={findings} truncated={scan.notices} />
          </section>

          {/* ── Refused — as visible as what gets fixed, and shown first ─── */}
          <section
            data-testid="id-bench-refused"
            data-walkthrough="idb-refused"
            className="rounded-2xl border border-destructive/40 bg-destructive/5 p-5"
          >
            <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
              <ShieldAlert className="h-4 w-4 text-destructive" />
              Refused — not touched ({count(plan.refused.length, "id")})
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              This tool never renumbers an id it cannot prove is safe to move. Every one of these was
              a candidate and every one has a reason it was left exactly as it is.
            </p>
            {plan.refused.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">Nothing was refused.</p>
            ) : (
              <ul className="mt-3 space-y-3">
                {plan.refused.slice(0, refusedShown).map((refusal, i) => (
                  <li key={i} className="rounded-lg border border-destructive/30 bg-background/70 p-3 text-sm">
                    <p className="font-medium text-foreground">
                      {labels.get(refusal.resource.toLowerCase()) ?? refusal.resource} —{" "}
                      {ID_POOLS[refusal.pool].label} id {refusal.id}
                    </p>
                    <p className="mt-1 text-muted-foreground">{refusal.reason}</p>
                  </li>
                ))}
              </ul>
            )}
            {plan.refused.length > refusedShown && (
              <button
                type="button"
                onClick={() => setRefusedShown((shown) => shown + ID_BENCH_ROWS_PAGE)}
                className="mt-3 text-sm font-medium text-primary hover:underline"
              >
                Show {n(Math.min(ID_BENCH_ROWS_PAGE, plan.refused.length - refusedShown))} more
              </button>
            )}
          </section>

          {/* ── The plan — every move, from and to, and why ──────────────── */}
          <section data-testid="id-bench-moves" data-walkthrough="idb-moves" className="rounded-2xl border border-border/60 bg-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-foreground">
                Renumber plan ({count(accepted.length, "id")} of {n(plan.moves.length)} selected)
              </h2>
              <div className="flex gap-3 text-sm">
                <button
                  type="button"
                  onClick={() => setDeselected(new Set<string>())}
                  className="font-medium text-primary hover:underline"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => setDeselected(new Set(plan.moves.map((move, i) => moveKey(move, i))))}
                  className="font-medium text-muted-foreground hover:underline"
                >
                  Select none
                </button>
              </div>
            </div>
            {plan.moves.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">Nothing needs renumbering.</p>
            ) : (
              <ul className="mt-3 space-y-3">
                {plan.moves.slice(0, movesShown).map((move, i) => {
                  const key = moveKey(move, i);
                  const isSelected = !deselected.has(key);
                  return (
                    <li key={key} className="rounded-lg border border-border/60 p-3 text-sm">
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          data-testid={`id-bench-move-${i}`}
                          checked={isSelected}
                          onChange={(event) => {
                            setDeselected((prev) => {
                              const next = new Set(prev);
                              if (event.target.checked) next.delete(key);
                              else next.add(key);
                              return next;
                            });
                          }}
                          className="mt-1 h-4 w-4 rounded border-border"
                        />
                        <span className="flex-1">
                          <span className="font-medium text-foreground">
                            {labels.get(move.resource.toLowerCase()) ?? move.resource} —{" "}
                            {ID_POOLS[move.pool].label}
                            {move.kitName ? ` "${move.kitName}"` : ""} {move.from} →{" "}
                            <span className="font-mono">{move.to}</span>
                          </span>
                          <span className="mt-1 block text-muted-foreground">Why it moved: {move.reason}</span>
                          <span className="mt-1 block text-muted-foreground">
                            Why {move.to}: {explainAllocation(move.pool, move.to)}
                          </span>
                          <span className="mt-1 block text-xs text-muted-foreground">
                            Files touched: {[...new Set(move.edits.map((e) => `${e.resource}/${e.edit.file}`))].join(", ")}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            {plan.moves.length > movesShown && (
              <button
                type="button"
                onClick={() => setMovesShown((shown) => shown + ID_BENCH_ROWS_PAGE)}
                className="mt-3 text-sm font-medium text-primary hover:underline"
              >
                Show {n(Math.min(ID_BENCH_ROWS_PAGE, plan.moves.length - movesShown))} more
              </button>
            )}
          </section>

          {/* ── Dry run — what the file looks like before it is written ──── */}
          {filesToDownload.length > 0 && (
            <section data-testid="id-bench-diffs" className="rounded-2xl border border-border/60 bg-card p-5">
              <h2 className="text-base font-semibold text-foreground">
                Preview the changes ({count(filesToDownload.length, "file")})
              </h2>
              <div className="mt-3 space-y-3">
                {filesToDownload.map((entry) => (
                  <details key={`${entry.resourceKey}/${entry.file}`} className="rounded-lg border border-border/60 p-3">
                    <summary className="cursor-pointer text-sm font-medium text-foreground">
                      {entry.resourceLabel} / {entry.file} ({count(entry.appliedCount, "change")})
                    </summary>
                    <pre className="mt-2 max-h-96 overflow-auto rounded bg-muted/60 p-3 text-xs leading-5 text-foreground/90">
                      {dryRunDiff(
                        entry.originalText,
                        acceptedEditsByResourceFile.get(`${entry.resourceKey}|${entry.file}`) ?? [],
                      )}
                    </pre>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* ── Before / after — the real number, not the requested one ──── */}
          <section
            data-testid="id-bench-before-after"
            className="rounded-2xl border border-border/60 bg-card p-5 text-sm"
          >
            <p className="text-foreground">
              Findings before: <span className="font-semibold">{findings.length}</span>. If you
              download and apply the {count(accepted.length, "selected move")} above: findings would
              become <span className="font-semibold">{afterFindings.length}</span>.
            </p>
            <p className="mt-1 text-muted-foreground">
              Computed by re-scanning the actual patched text of every changed file — not by counting
              what the plan intended to fix.
            </p>
            {rejectedAtWriteTime.length > 0 && (
              <div className="mt-3 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
                <p className="font-medium text-destructive">
                  {count(rejectedAtWriteTime.length, "edit")} could not be verified against the current
                  text and were not applied:
                </p>
                <ul className="mt-1 space-y-1 text-muted-foreground">
                  {rejectedAtWriteTime.map((entry, i) => (
                    <li key={i}>
                      {entry.resource} / {entry.file}: {entry.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* ── Download ──────────────────────────────────────────────────── */}
          <section data-testid="id-bench-download" data-walkthrough="idb-download" className="rounded-2xl border border-border/60 bg-card p-5">
            <h2 className="text-base font-semibold text-foreground">Download</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              The zip holds only the {count(filesToDownload.length, "file")} an accepted move actually
              changed, each paired with a <code className="font-mono">.bak</code> of the original,
              plus a Markdown report and a JSON change manifest — every id moved, from what to what,
              in which file.
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              <button
                type="button"
                data-testid="id-bench-download-zip"
                onClick={() => void downloadZip()}
                disabled={filesToDownload.length === 0 || rescanning}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                Download changed files (.zip)
              </button>
              <button
                type="button"
                data-testid="id-bench-download-report"
                onClick={downloadReport}
                disabled={rescanning}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileText className="h-4 w-4" />
                Report only (.md)
              </button>
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <FileJson className="h-3.5 w-3.5" />
                Manifest is bundled in the zip
              </span>
            </div>
            {downloadError && (
              <p className="mt-3 flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4" /> {downloadError}
              </p>
            )}
          </section>
        </>
      )}

      {/* Manual mode. The signal is raised by the sample button and by the
          replay button in the intake row — never by a report appearing,
          because a report appearing cannot tell this component whose folder
          it came from, and a dimmer over a stranger's own ids is an
          interruption. The sample-announcing step is dropped when no sample
          is loaded, by `id` so reordering the array cannot drop another. */}
      <ToolWalkthrough
        steps={demo ? idBenchSteps : idBenchSteps.filter((step) => step.id !== "welcome")}
        storageKey="workshop_tour_id_bench_v1"
        finishLabel={idBenchFinishLabel}
        openSignal={tourSignal}
      />
    </div>
  );
}
